import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Card, CardContent, Typography, Table, TableBody, TableCell,
  TableHead, TableRow, IconButton, CircularProgress, Chip,
  Button, Dialog, DialogTitle, DialogContent, DialogContentText,
  DialogActions, Divider
} from '@mui/material';
import {
  Home, Add, Edit, Delete, RequestQuote, Send, CheckCircle,
  Cancel, ShoppingCart
} from '@mui/icons-material';
import axios from 'axios';
import Toast from './Toast';
import { fmt, sym } from '../utils/currency';

const API = process.env.REACT_APP_API_URL;

const STATUS_COLOR = {
  Draft: 'default', Sent: 'info', Accepted: 'primary',
  Rejected: 'error', Converted: 'success', Cancelled: 'default'
};

const InfoRow = ({ label, value }) => (
  <Box display="flex" gap={1} mb={0.5}>
    <Typography variant="body2" color="text.secondary" sx={{ minWidth: 160, fontWeight: 500 }}>{label}</Typography>
    <Typography variant="body2">{value || <em style={{ color: '#bbb' }}>—</em>}</Typography>
  </Box>
);

const PurchaseQuoteList = () => {
  const navigate = useNavigate();
  const [quotes,   setQuotes]   = useState([]);
  const [error,    setError]    = useState('');
  const [success,  setSuccess]  = useState('');
  const [loading,  setLoading]  = useState(true);
  const [viewQuote, setViewQuote] = useState(null);
  const [convertQuote, setConvertQuote] = useState(null);
  const [cancelQuote,  setCancelQuote]  = useState(null);
  const [busy, setBusy] = useState(false);

  const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };

  const fetchQuotes = async () => {
    try {
      const { data } = await axios.get(`${API}/api/payables/quotes`, { headers });
      setQuotes(data);
      setError('');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load quotes');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchQuotes(); }, []); // eslint-disable-line

  const setStatus = async (quoteNo, status) => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/api/payables/quotes/${quoteNo}/status`, { status }, { headers });
      setSuccess(data.message || 'Updated');
      fetchQuotes();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update quote');
    } finally {
      setBusy(false);
    }
  };

  const handleConvert = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/api/payables/quotes/${convertQuote.quoteNo}/convert`, {}, { headers });
      setConvertQuote(null);
      setSuccess(data.message || 'Converted to order');
      fetchQuotes();
      // Jump straight to the new PO so the user continues the flow
      if (data.orderNo) navigate('/payables/orders');
    } catch (err) {
      setConvertQuote(null);
      setError(err.response?.data?.error || 'Failed to convert quote');
    } finally {
      setBusy(false);
    }
  };

  const handleCancel = async () => {
    try {
      await axios.delete(`${API}/api/payables/quotes/${cancelQuote}`, { headers });
      setCancelQuote(null);
      setSuccess('Quote cancelled');
      fetchQuotes();
    } catch (err) {
      setCancelQuote(null);
      setError(err.response?.data?.error || 'Failed to cancel quote');
    }
  };

  const canEdit    = (s) => ['Draft', 'Sent'].includes(s);
  const canConvert = (s) => ['Draft', 'Sent', 'Accepted'].includes(s);

  if (loading) return (
    <Box sx={{ maxWidth: 1100, mx: 'auto', mt: 4, textAlign: 'center' }}>
      <CircularProgress /><Typography sx={{ mt: 2 }}>Loading quotes...</Typography>
    </Box>
  );

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', mt: 4 }}>
      <Card elevation={3}>
        <CardContent>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
            <Box display="flex" alignItems="center">
              <RequestQuote sx={{ mr: 1, color: 'primary.main' }} />
              <Typography variant="h5" color="primary">Purchase Quotes</Typography>
            </Box>
            <Box>
              <IconButton onClick={() => navigate('/payables')} title="Payables Home"
                sx={{ bgcolor: 'background.paper', '&:hover': { bgcolor: 'grey.100' }, mr: 1 }}>
                <Home />
              </IconButton>
              <IconButton onClick={() => navigate('/payables/quotes/new')} title="New Quote"
                sx={{ bgcolor: 'background.paper', '&:hover': { bgcolor: 'grey.100' } }}>
                <Add />
              </IconButton>
            </Box>
          </Box>

          <Toast error={error} success={success} onClose={(k) => (k === 'error' ? setError('') : setSuccess(''))} />

          {quotes.length === 0 ? <Typography>No purchase quotes found. Create one to start the procurement flow.</Typography> : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell><strong>Quote No</strong></TableCell>
                  <TableCell><strong>Supplier</strong></TableCell>
                  <TableCell><strong>Date</strong></TableCell>
                  <TableCell><strong>Valid Until</strong></TableCell>
                  <TableCell align="center"><strong>Lines</strong></TableCell>
                  <TableCell align="right"><strong>Total ({sym()})</strong></TableCell>
                  <TableCell><strong>Status</strong></TableCell>
                  <TableCell><strong>Actions</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {quotes.map(q => (
                  <TableRow key={q.quoteNo} hover sx={{ cursor: 'pointer' }}
                    onClick={() => setViewQuote(q)}>
                    <TableCell><strong>{q.quoteNo}</strong></TableCell>
                    <TableCell>{q.supplierCode}</TableCell>
                    <TableCell>{q.quoteDate ? new Date(q.quoteDate).toLocaleDateString() : ''}</TableCell>
                    <TableCell>{q.expiryDate ? new Date(q.expiryDate).toLocaleDateString() : '—'}</TableCell>
                    <TableCell align="center" onClick={e => e.stopPropagation()}>
                      <Chip label={q.lines?.length || 0} size="small" color="info"
                        variant="outlined" onClick={() => setViewQuote(q)}
                        sx={{ cursor: 'pointer', fontWeight: 700, minWidth: 40 }} />
                    </TableCell>
                    <TableCell align="right">{(q.totalAmount || 0).toFixed(2)}</TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={0.5} flexWrap="wrap">
                        <Chip label={q.status} color={STATUS_COLOR[q.status] || 'default'}
                          size="small" variant="outlined" />
                        {q.orderNo && (
                          <Chip label={q.orderNo} size="small" color="success"
                            sx={{ fontSize: '0.65rem', height: 18 }} />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell onClick={e => e.stopPropagation()}>
                      <Box display="flex" alignItems="center">
                        {canEdit(q.status) && (
                          <IconButton color="primary" size="small"
                            onClick={() => navigate(`/payables/quotes/${q.quoteNo}/edit`)} title="Edit">
                            <Edit fontSize="small" />
                          </IconButton>
                        )}
                        {q.status === 'Draft' && (
                          <IconButton color="info" size="small"
                            onClick={() => setStatus(q.quoteNo, 'Sent')} title="Mark Sent (RFQ issued)" disabled={busy}>
                            <Send fontSize="small" />
                          </IconButton>
                        )}
                        {q.status === 'Sent' && (
                          <IconButton color="primary" size="small"
                            onClick={() => setStatus(q.quoteNo, 'Accepted')} title="Accept Quote" disabled={busy}>
                            <CheckCircle fontSize="small" />
                          </IconButton>
                        )}
                        {q.status === 'Sent' && (
                          <IconButton color="error" size="small"
                            onClick={() => setStatus(q.quoteNo, 'Rejected')} title="Reject Quote" disabled={busy}>
                            <Cancel fontSize="small" />
                          </IconButton>
                        )}
                        {canConvert(q.status) && (
                          <IconButton color="success" size="small"
                            onClick={() => setConvertQuote(q)} title="Convert to Purchase Order">
                            <ShoppingCart fontSize="small" />
                          </IconButton>
                        )}
                        {q.status !== 'Converted' && (
                          <IconButton color="error" size="small"
                            onClick={() => setCancelQuote(q.quoteNo)} title="Cancel">
                            <Delete fontSize="small" />
                          </IconButton>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* ── Quote Card (view) ── */}
      <Dialog open={Boolean(viewQuote)} onClose={() => setViewQuote(null)} maxWidth="md" fullWidth>
        {viewQuote && (
          <>
            <DialogTitle component="div">
              <Box display="flex" alignItems="center" gap={1}>
                <RequestQuote color="primary" />
                <Box>
                  <Typography variant="h6" lineHeight={1}>{viewQuote.quoteNo}</Typography>
                  <Typography variant="caption" color="text.secondary">Purchase Quote</Typography>
                </Box>
                <Box ml="auto">
                  <Chip label={viewQuote.status} color={STATUS_COLOR[viewQuote.status] || 'default'}
                    size="small" variant="outlined" />
                </Box>
              </Box>
            </DialogTitle>
            {(canEdit(viewQuote.status) || canConvert(viewQuote.status)) && (
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center',
                px: 3, py: 1.25, borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'grey.50' }}>
                {canEdit(viewQuote.status) && (
                  <Button size="small" color="primary" startIcon={<Edit />}
                    onClick={() => { setViewQuote(null); navigate(`/payables/quotes/${viewQuote.quoteNo}/edit`); }}>
                    Edit
                  </Button>
                )}
                {canConvert(viewQuote.status) && (
                  <Button size="small" color="success" variant="outlined" startIcon={<ShoppingCart />}
                    onClick={() => { const q = viewQuote; setViewQuote(null); setConvertQuote(q); }}>
                    Convert to Order
                  </Button>
                )}
              </Box>
            )}
            <DialogContent dividers>
              <Typography variant="subtitle2" color="primary" gutterBottom>Document</Typography>
              <InfoRow label="Quote No"      value={viewQuote.quoteNo} />
              <InfoRow label="Quote Date"    value={viewQuote.quoteDate ? new Date(viewQuote.quoteDate).toLocaleDateString() : ''} />
              <InfoRow label="Valid Until"   value={viewQuote.expiryDate ? new Date(viewQuote.expiryDate).toLocaleDateString() : ''} />
              <InfoRow label="Vendor Quote Ref" value={viewQuote.supplierQuoteRef} />
              {viewQuote.orderNo && <InfoRow label="Converted to Order" value={viewQuote.orderNo} />}

              <Divider sx={{ my: 1.5 }} />
              <Typography variant="subtitle2" color="primary" gutterBottom>Supplier</Typography>
              <InfoRow label="Supplier Code" value={viewQuote.supplierCode} />

              <Divider sx={{ my: 1.5 }} />
              <Typography variant="subtitle2" color="primary" gutterBottom>
                Line Items ({viewQuote.lines?.length || 0})
              </Typography>
              {viewQuote.lines && viewQuote.lines.length > 0 ? (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700, py: 0.8 }}>#</TableCell>
                      <TableCell sx={{ fontWeight: 700, py: 0.8 }}>Type</TableCell>
                      <TableCell sx={{ fontWeight: 700, py: 0.8 }}>Item / FA / GL</TableCell>
                      <TableCell sx={{ fontWeight: 700, py: 0.8 }}>Description</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, py: 0.8 }}>Qty</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, py: 0.8 }}>Unit Price</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, py: 0.8 }}>VAT</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, py: 0.8 }}>Amount</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {viewQuote.lines.map((line, idx) => (
                      <TableRow key={idx}>
                        <TableCell sx={{ py: 0.6 }}>{line.lineNo || idx + 1}</TableCell>
                        <TableCell sx={{ py: 0.6 }}>{line.lineType}</TableCell>
                        <TableCell sx={{ py: 0.6 }}>{line.itemNo || line.faNo || line.glAccount || '—'}</TableCell>
                        <TableCell sx={{ py: 0.6, maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis' }}
                          title={line.description}>{line.description}</TableCell>
                        <TableCell align="right" sx={{ py: 0.6 }}>{line.qty}</TableCell>
                        <TableCell align="right" sx={{ py: 0.6 }}>{fmt(line.unitPrice)}</TableCell>
                        <TableCell align="right" sx={{ py: 0.6 }}
                          title={line.vatPercent ? `${line.vatPercent}%` : ''}>
                          {line.vatAmount ? fmt(line.vatAmount) : '—'}
                        </TableCell>
                        <TableCell align="right" sx={{ py: 0.6, fontWeight: 600 }}>{fmt(line.amount)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <Typography variant="body2" color="text.secondary">No lines on this quote.</Typography>
              )}

              <Divider sx={{ my: 1.5 }} />
              <Box display="flex" flexDirection="column" alignItems="flex-end" gap={0.3}>
                <Typography variant="body2" color="text.secondary">
                  Subtotal: {sym()} {fmt(viewQuote.subtotal || 0)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  VAT: {sym()} {fmt(viewQuote.totalVAT || 0)}
                </Typography>
                <Typography variant="body1" fontWeight={700}>
                  Total: {sym()} {fmt(viewQuote.totalAmount || 0)}
                </Typography>
              </Box>
            </DialogContent>
            <DialogActions>
              <Button variant="outlined" onClick={() => setViewQuote(null)}>Close</Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Convert confirm */}
      <Dialog open={Boolean(convertQuote)} onClose={() => setConvertQuote(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Convert Quote {convertQuote?.quoteNo}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This creates a Purchase Order from quote <strong>{convertQuote?.quoteNo}</strong>
            {' '}({sym()} {fmt(convertQuote?.totalAmount)}). The quote is marked <strong>Converted</strong>
            {' '}and locked. Receiving and invoicing — and all G/L — happen on the new order.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConvertQuote(null)}>Cancel</Button>
          <Button variant="contained" color="success" onClick={handleConvert} disabled={busy}>
            {busy ? 'Converting…' : 'Convert to Order'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Cancel confirm */}
      <Dialog open={Boolean(cancelQuote)} onClose={() => setCancelQuote(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Cancel Quote {cancelQuote}</DialogTitle>
        <DialogContent>
          <DialogContentText>Are you sure you want to cancel quote {cancelQuote}?</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelQuote(null)}>No</Button>
          <Button variant="contained" color="error" onClick={handleCancel}>Yes, Cancel</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PurchaseQuoteList;
